import React from 'react';

import { ChevronLeft, ChevronRight, ReceiptText, RefreshCw } from 'lucide-react';

import Modal from '@/components/common/Modal';
import type { PaymentOrderListItem, PaymentOrdersResponse } from '@/types';

interface PaymentOrdersModalProps {
  open: boolean;
  orders: PaymentOrdersResponse | null;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  onClose: () => void;
  onBack: () => void;
  onRetry: () => void;
  onPageChange: (page: number) => void;
}

const orderTableColumns = 'grid-cols-[minmax(0,1.6fr)_110px_110px_90px_1fr]';
const orderTableMinWidth = 'min-w-[760px]';

const formatAmount = (amountCent: number, currency = 'CNY'): string => {
  const symbol = currency === 'CNY' ? '¥' : `${currency} `;
  return `${symbol}${(amountCent / 100).toFixed(2)}`;
};

const timezoneSuffixPattern = /(Z|[+-]\d{2}:?\d{2})$/i;

const formatTime = (value?: string | null): string => {
  if (!value) return '--';
  const trimmed = value.trim();
  const normalized = trimmed.replace(' ', 'T');
  const timestamp = timezoneSuffixPattern.test(normalized)
    ? normalized
    : `${normalized}${normalized.includes('T') ? 'Z' : 'T00:00:00Z'}`;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return value;
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day} ${hour}:${parts.minute}:${parts.second}`;
};

const formatPoints = (points: number): string => `${points}积分`;

const normalizePointsCopy = (value: string, points: number): string => {
  return value.replace(new RegExp(`${points}\\s*点`, 'g'), formatPoints(points));
};

const getProductName = (order: PaymentOrderListItem): string => {
  const name = order.product_snapshot?.name || `ManClaw 积分充值 ${formatPoints(order.points)}`;
  return normalizePointsCopy(name, order.points);
};

const getPaymentMethodName = (order: PaymentOrderListItem): string => {
  const method = order.payment_method;
  if (!method) return '--';
  const methodText = `${method.method_id} ${method.name}`.toLowerCase();
  if (
    methodText.includes('wechat') ||
    methodText.includes('weixin') ||
    method.name.includes('微信')
  ) {
    return '微信支付';
  }
  if (methodText.includes('alipay') || method.name.includes('支付宝')) {
    return '支付宝支付';
  }
  return method.name.endsWith('支付') ? method.name : `${method.name}支付`;
};

const PaymentOrdersModal: React.FC<PaymentOrdersModalProps> = ({
  open,
  orders,
  loading,
  error,
  page,
  pageSize,
  onClose,
  onBack,
  onRetry,
  onPageChange,
}) => {
  const items = orders?.items ?? [];
  const total = orders?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Modal
      title="充值记录"
      titleCenter
      open={open}
      onClose={onClose}
      onBack={onBack}
      width="min(980px, calc(100vw - 48px))"
      bodyClassName="flex flex-col"
      bodyStyle={{ height: 'min(620px, calc(100vh - 180px))', overflow: 'hidden' }}
    >
      <div data-testid="payment-orders-records" className="min-h-0 flex flex-1 flex-col pt-2">
        {loading && !orders ? (
          <div className="space-y-3 overflow-y-auto">
            <div className="h-10 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
            <div className="h-20 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
            <div className="h-20 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
          </div>
        ) : error ? (
          <div className="flex h-56 flex-col items-center justify-center gap-3 rounded-lg border border-dark-border bg-dark-card">
            <p className="text-sm font-medium text-dark-text">充值记录加载失败</p>
            <p className="max-w-sm text-center text-sm text-dark-text-secondary">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-theme-color px-4 text-sm text-theme-color transition-colors hover:bg-theme-color-10"
            >
              <RefreshCw className="h-4 w-4" />
              重试
            </button>
          </div>
        ) : (
          <div className="min-h-0 flex flex-1 flex-col overflow-hidden">
            {items.length > 0 ? (
              <div className="h-0 flex-1 overflow-auto rounded-lg border border-dark-border">
                <div
                  className={`sticky top-0 z-10 grid ${orderTableMinWidth} ${orderTableColumns} border-b border-dark-border bg-dark-card text-xs font-medium text-dark-text-secondary`}
                  style={{ backgroundColor: 'rgb(var(--color-dark-card))' }}
                >
                  <span className="whitespace-nowrap px-4 py-2">商品</span>
                  <span className="whitespace-nowrap px-4 py-2 text-center">支付方式</span>
                  <span className="whitespace-nowrap px-4 py-2 text-center">金额</span>
                  <span className="whitespace-nowrap px-4 py-2 text-center">积分</span>
                  <span className="whitespace-nowrap px-4 py-2 text-center">支付时间</span>
                </div>
                {items.map((order) => (
                  <div
                    key={order.order_id}
                    className={`grid ${orderTableMinWidth} ${orderTableColumns} items-center border-b border-dark-border last:border-b-0`}
                  >
                    <div className="min-w-0 px-4 py-3">
                      <p className="truncate text-sm font-medium text-dark-text">
                        {getProductName(order)}
                      </p>
                    </div>
                    <span className="px-4 py-3 text-center text-sm font-medium text-dark-text">
                      {getPaymentMethodName(order)}
                    </span>
                    <span className="px-4 py-3 text-center text-sm font-semibold text-theme-color tabular-nums">
                      {formatAmount(order.amount_cent, order.currency)}
                    </span>
                    <span className="px-4 py-3 text-center text-sm font-medium text-dark-text">
                      {formatPoints(order.points)}
                    </span>
                    <span className="px-4 py-3 text-center text-xs text-dark-text-secondary">
                      {formatTime(order.paid_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex h-48 flex-col items-center justify-center rounded-lg border border-dark-border bg-dark-card text-center">
                <ReceiptText className="mb-3 h-7 w-7 text-dark-text-secondary" />
                <p className="text-sm font-medium text-dark-text">暂无充值记录</p>
                <p className="mt-1 text-xs text-dark-text-secondary">当前账号还没有成功充值订单</p>
              </div>
            )}

            <div className="shrink-0 flex flex-wrap items-center justify-center gap-3 pt-4 text-xs text-dark-text-secondary">
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={loading || page <= 1}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-dark-border px-3 transition-colors hover:border-theme-color hover:text-dark-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                上一页
              </button>
              <span className="inline-flex items-center gap-1 tabular-nums">
                第 {page} / {totalPages} 页，共 {total} 条
              </span>
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={loading || page >= totalPages}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-dark-border px-3 transition-colors hover:border-theme-color hover:text-dark-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                下一页
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PaymentOrdersModal;
